// src/components/EnrichmentsView.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardMedia,
    Paper,
    IconButton,
    Tooltip,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useTranslation } from 'react-i18next';
import { getEnrichmentsByDate, deleteEnrichment, toggleEnrichmentStatus } from '../services/liturgicalService';
import { DailyEnrichment } from '../types/liturgical';

interface EnrichmentsViewProps {
    day: number;
    month: number;
    isAdmin?: boolean;
    onRefresh?: () => void;
}

const EnrichmentsView: React.FC<EnrichmentsViewProps> = ({ day, month, isAdmin = true, onRefresh }) => {
    const { t } = useTranslation();
    const [enrichments, setEnrichments] = useState<DailyEnrichment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [enrichmentToDelete, setEnrichmentToDelete] = useState<DailyEnrichment | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const enrichmentsRes = await getEnrichmentsByDate(month, day);
            setEnrichments(enrichmentsRes || []);
        } catch (err: any) {
            console.error('Failed to fetch daily enrichments:', err);
            if (err.response?.status !== 404) {
                setError('Failed to fetch daily enrichments. See console for details.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [day, month]);

    const handleToggleStatus = async (enrichment: DailyEnrichment) => {
        setActionLoading(true);
        try {
            await toggleEnrichmentStatus(enrichment.id);
            fetchData();
            onRefresh?.();
        } catch (err: any) {
            console.error('Failed to toggle enrichment status:', err);
            setError(t('admin.enrichments.errorToggling'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteClick = (enrichment: DailyEnrichment) => {
        setEnrichmentToDelete(enrichment);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!enrichmentToDelete) return;
        setActionLoading(true);
        try {
            await deleteEnrichment(enrichmentToDelete.id);
            setDeleteDialogOpen(false);
            setEnrichmentToDelete(null);
            fetchData();
            onRefresh?.();
        } catch (err: any) {
            console.error('Failed to delete enrichment:', err);
            setError(t('admin.enrichments.errorDeleting'));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>{t('admin.enrichments.title')} {day}/{month}</Typography>
            {enrichments.length > 0 ? (
                enrichments.map(enrichment => (
                    <Card key={enrichment.id} sx={{ mb: 3 }}>
                        {/* Admin Actions Header */}
                        {isAdmin && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.100' }}>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        label={enrichment.is_active ? t('admin.enrichments.active') : t('admin.enrichments.inactive')}
                                        color={enrichment.is_active ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={enrichment.is_active ? t('admin.enrichments.deactivate') : t('admin.enrichments.activate')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleStatus(enrichment)}
                                            disabled={actionLoading}
                                        >
                                            {enrichment.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('common.delete')}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteClick(enrichment)}
                                            disabled={actionLoading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        )}
                        {/* Image du Saint */}
                        {enrichment.saint_image_url && (
                            <CardMedia
                                component="img"
                                height="200"
                                image={enrichment.saint_image_url}
                                alt={enrichment.saint_name || 'Saint'}
                            />
                        )}
                        <CardContent>
                            {/* Section Saint */}
                            {enrichment.saint_name && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="h6" color="primary">
                                        {enrichment.saint_name}
                                    </Typography>
                                    {enrichment.saint_biography && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {enrichment.saint_biography}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Section Écriture */}
                            {enrichment.scripture_text && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    {enrichment.scripture_reference && (
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {enrichment.scripture_reference}
                                        </Typography>
                                    )}
                                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                                        "{enrichment.scripture_text}"
                                    </Typography>
                                </Box>
                            )}

                            {/* Section Méditation */}
                            {enrichment.meditation_text && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Méditation
                                    </Typography>
                                    <Typography variant="body1">
                                        {enrichment.meditation_text}
                                    </Typography>
                                </Box>
                            )}

                            {/* Section Prière */}
                            {enrichment.prayer_text && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Prière
                                    </Typography>
                                    <Typography variant="body1">
                                        {enrichment.prayer_text}
                                    </Typography>
                                </Box>
                            )}

                            {/* Médias Audio/Vidéo */}
                            {enrichment.audio_url && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Audio de Méditation</Typography>
                                    <audio controls style={{ width: '100%' }}>
                                        <source src={enrichment.audio_url} type="audio/mpeg" />
                                        Votre navigateur ne supporte pas la lecture audio.
                                    </audio>
                                </Box>
                            )}

                            {enrichment.video_url && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Vidéo de Méditation</Typography>
                                    <video controls style={{ width: '100%', maxHeight: '400px' }}>
                                        <source src={enrichment.video_url} type="video/mp4" />
                                        Votre navigateur ne supporte pas la lecture vidéo.
                                    </video>
                                </Box>
                            )}

                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                                Créé par {enrichment.created_by?.name || 'Utilisateur inconnu'} le {new Date(enrichment.created_at).toLocaleDateString()}
                            </Typography>
                        </CardContent>
                    </Card>
                ))
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('admin.enrichments.noEnrichments')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('admin.enrichments.noEnrichments')} {day}/{month}
                    </Typography>
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !actionLoading && setDeleteDialogOpen(false)}
            >
                <DialogTitle>{t('admin.enrichments.deleteConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('admin.enrichments.deleteConfirmMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={actionLoading}
                    >
                        {actionLoading ? t('common.loading') : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnrichmentsView;
